/**
 * CameraController — ArcRotateCamera orbit / zoom / pan for the editor scene.
 */

import { ArcRotateCamera, Vector3, type Scene, type AbstractMesh } from 'babylonjs';
import { PANELS_POSITION, ORBIT_POSITION } from '@shared/constants';

const MIN_BETA = 0.05;
const MAX_BETA = Math.PI / 2 + 0.2; // allow slightly below horizon, clamp under-floor
// Pixels→world divisor for target panning (lower = faster pan). Tuned to feel
// natural at the default ~6-unit orbit radius.
const PANNING_SENSIBILITY = 250;

export class CameraController {
  readonly camera: ArcRotateCamera;
  private canvas: HTMLCanvasElement;
  private target = new Vector3(PANELS_POSITION.x, PANELS_POSITION.y, PANELS_POSITION.z);

  constructor(scene: Scene, canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.camera = new ArcRotateCamera('editorCamera', 0, Math.PI / 3, 6, this.target.clone(), scene);
    this.camera.setPosition(new Vector3(ORBIT_POSITION.x, ORBIT_POSITION.y, ORBIT_POSITION.z));
    this.camera.fov = (75 * Math.PI) / 180; // 75° vertical FOV
    // Near/far clip planes: Babylon's default minZ (~1) clips objects too early
    // when zooming in close, so widen to 0.1–1000.
    this.camera.minZ = 0.1;
    this.camera.maxZ = 1000;
    this.camera.lowerRadiusLimit = 1;
    this.camera.upperRadiusLimit = 15;
    this.camera.lowerBetaLimit = MIN_BETA;
    this.camera.upperBetaLimit = MAX_BETA;
    this.camera.wheelPrecision = 30;
    // Enable target panning (drag the scene), driven by the pan modifier below.
    this.camera.panningSensibility = PANNING_SENSIBILITY;
    this.camera.panningInertia = 0.9;
    this.camera.attachControl(canvas, true);
    this._enablePanModifier();
  }

  /**
   * "Cmd/Ctrl + drag to pan". Babylon's ArcRotateCamera
   * already pans on drag when its pointer input sees `_ctrlKey` (with
   * `_useCtrlForPanning`), but it only ever reads `event.ctrlKey` — so on macOS
   * the Cmd key (metaKey) doesn't pan. Babylon DOES record `_metaKey` from every
   * pointer event though, so we alias the input's private `_ctrlKey` flag to also
   * report true when meta is held. Result: Cmd (mac) or Ctrl (win/linux) + drag
   * pans; a plain drag still orbits. (Pinned babylonjs 7.45.0 internals.)
   */
  private _enablePanModifier(): void {
    const pointers = this.camera.inputs.attached.pointers as unknown as {
      _ctrlKey: boolean;
      _metaKey: boolean;
      __ejCtrl?: boolean;
    } | undefined;
    if (!pointers) return;
    Object.defineProperty(pointers, '_ctrlKey', {
      configurable: true,
      get(this: { __ejCtrl?: boolean; _metaKey: boolean }) {
        return Boolean(this.__ejCtrl) || Boolean(this._metaKey);
      },
      set(this: { __ejCtrl?: boolean }, value: boolean) {
        this.__ejCtrl = value;
      },
    });
  }

  enable(): void {
    this.camera.attachControl(this.canvas, true);
  }

  disable(): void {
    this.camera.detachControl();
  }

  focusElement(node: AbstractMesh | import('babylonjs').TransformNode): void {
    const pos = (node as { getAbsolutePosition?: () => Vector3 }).getAbsolutePosition?.()
      ?? (node as { position: Vector3 }).position;
    this.camera.setTarget(pos.clone());
  }

  reset(): void {
    this.camera.setTarget(new Vector3(PANELS_POSITION.x, PANELS_POSITION.y, PANELS_POSITION.z));
    this.camera.setPosition(new Vector3(ORBIT_POSITION.x, ORBIT_POSITION.y, ORBIT_POSITION.z));
  }

  update(): void {
    // ArcRotateCamera updates itself; nothing per-frame required.
  }

  dispose(): void {
    this.camera.dispose();
  }
}

export default CameraController;
